import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { roleService } from '../services/roleService';
import type { Role, CreateRoleRequest, UpdateRoleRequest } from '../services/roleService';
import { useNotification } from '../contexts/NotificationContext';

export const useRoles = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  const rolesQuery = useQuery({
    queryKey: ['roles'],
    queryFn: roleService.getRoles,
  });

  const createRoleMutation = useMutation({
    mutationFn: (data: CreateRoleRequest) => roleService.createRole(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      showNotification({
        type: 'success',
        title: 'Role Created',
        message: 'The role has been created successfully.',
      });
    },
    onError: (error: any) => {
      showNotification({
        type: 'error',
        title: 'Failed to Create Role',
        message: error.response?.data?.error || 'Could not create the role.',
      });
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateRoleRequest }) => 
      roleService.updateRole(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      showNotification({
        type: 'success',
        title: 'Role Updated',
        message: 'The role has been updated successfully.',
      });
    },
    onError: (error: any) => {
      showNotification({
        type: 'error',
        title: 'Failed to Update Role',
        message: error.response?.data?.error || 'Could not update the role.',
      });
    },
  });

  const deleteRoleMutation = useMutation({
    mutationFn: (id: number) => roleService.deleteRole(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      showNotification({
        type: 'success',
        title: 'Role Deleted',
        message: 'The role has been deleted successfully.',
      });
    },
    onError: (error: any) => {
      showNotification({
        type: 'error',
        title: 'Failed to Delete Role',
        message: error.response?.data?.error || 'Could not delete the role.',
      });
    },
  });

  return {
    roles: rolesQuery.data || [],
    isLoading: rolesQuery.isLoading,
    error: rolesQuery.error,
    createRole: createRoleMutation.mutate,
    updateRole: updateRoleMutation.mutate,
    deleteRole: deleteRoleMutation.mutate,
    isCreating: createRoleMutation.isPending,
    isUpdating: updateRoleMutation.isPending,
    isDeleting: deleteRoleMutation.isPending,
  };
};

export const useRole = (roleId: number) => {
  const roleQuery = useQuery({
    queryKey: ['roles', roleId],
    queryFn: () => roleService.getRole(roleId),
    enabled: !!roleId,
  });

  return {
    role: roleQuery.data,
    isLoading: roleQuery.isLoading,
    error: roleQuery.error,
  };
};

export const usePermissions = () => {
  const permissionsQuery = useQuery({
    queryKey: ['permissions'],
    queryFn: roleService.getPermissions,
  });

  return {
    permissions: permissionsQuery.data || [],
    isLoading: permissionsQuery.isLoading,
    error: permissionsQuery.error,
  };
};